import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {AnalyticsScalarMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {getHasAnalyticsResolutionMetrics} from '../../../../store/featureFlags/featureFlagsSelectors';
import {AnalyticsReportsEnum, getReportElementMetrics} from '../../../helpers/analyticsReportHelpers';
import {useAnalyticsHeroMetricPicker} from '../../../helpers/hooks/useAnalyticsHeroMetricPicker';
import {ReportGrid} from '../../reportViewer/reportGrid';
import {HeroMetricSubtitle} from '../../reportViewer/reportHeroMetrics';
import {ReportElementWrapper, ReportElementWrapperProps} from '../reportElement';
import {AnalyticsHeroCard} from './analyticsHeroCard';
import {CardMetricPicker} from './cardMetricPicker';

/*
 * Const.
 */

const DEFAULT_MAX_CARDS = 4;
const DEFAULT_TITLE = <Trans id="analytics-hero-cards-default-title">Main Metrics</Trans>;
const DEFAULT_SUBTITLE = <HeroMetricSubtitle />;

/*
 * Props.
 */

export interface ReportHeroCardsProps extends ReportElementWrapperProps {
  reportType: AnalyticsReportsEnum;
  isLoading: boolean;
  maxCards?: number;
  // For attaching a Pendo element.
  interactionId?: string;
}

/*
 * Component.
 */

export const ReportHeroCards: FC<ReportHeroCardsProps> = ({
  title,
  subtitle,
  reportType,
  reportSectionId,
  interactionId,
  maxCards = DEFAULT_MAX_CARDS,
  isLoading,
}) => {
  const hasResolutionMetrics = useAppSelector(getHasAnalyticsResolutionMetrics);

  const {displayedMetrics, availableMetrics} = useMemo(
    () =>
      getReportElementMetrics<AnalyticsScalarMetricsEnum>(reportType, reportSectionId, hasResolutionMetrics),
    [hasResolutionMetrics, reportSectionId, reportType],
  );

  const [metricPickerMetrics, onMetricPickerEntryClick] = useAnalyticsHeroMetricPicker(
    displayedMetrics,
    reportSectionId,
  );

  const maybeRenderSettingsDropdown = useMemo(
    () =>
      availableMetrics.length > maxCards
        ? (popoverProps: PopoverTransferProps) => (
            <CardMetricPicker
              metrics={availableMetrics}
              displayedMetrics={metricPickerMetrics}
              onMetricClick={onMetricPickerEntryClick}
              maxCards={maxCards}
              {...popoverProps}
            />
          )
        : undefined,
    [availableMetrics, metricPickerMetrics, maxCards, onMetricPickerEntryClick],
  );

  return (
    <ReportElementWrapper
      reportSectionId={reportSectionId}
      title={title || DEFAULT_TITLE}
      subtitle={subtitle || DEFAULT_SUBTITLE}
      renderSettingsDropdown={maybeRenderSettingsDropdown}
    >
      <ReportGrid>
        {availableMetrics
          .filter((metric) => metricPickerMetrics.includes(metric))
          .map((metric, index) => (
            <AnalyticsHeroCard
              key={metric}
              isLoading={isLoading}
              metric={metric}
              // We must pass interactionId directly to AnalyticsCard, to anchor it to a child element.
              interactionId={index === 0 ? interactionId : undefined}
            />
          ))}
      </ReportGrid>
    </ReportElementWrapper>
  );
};
